import { Gap, P, SvgIcon, theme } from '@salecycle/ui-component-library';
import { useMemo } from 'react';
import { parseExitIntentAreas } from 'sections/onsite-campaigns/pages/CampaignEditor/tabs/CampaignWorkflowTab/components/Workflows/utils';
import { useTranslation } from 'react-i18next';
import { TriggerType, ExitIntentAreas } from 'sections/onsite-campaigns/types';
import {
  AtLeastOneTriggerRequiredRule,
  AtLeastOneExitIntentRequiredRule,
  InactivityRequiredIfSelectedRule,
  FrequencyRequiredRule,
} from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules';
import { useCanvas } from '../../Canvas/hooks/useCanvas';
import { useDrawer } from '../../../state/Drawer/hooks/useDrawer';
import { useTrigger } from './hooks/useTrigger';
import { useWorkflowForm } from '../../../contexts/CampaignWorkflowFormContext';
import { WorkflowLabel } from '../../WorkflowBox/WorkflowLabel';
import { WorkflowBoxHeader } from '../../WorkflowBox/WorkflowBoxHeader';
import { WorkflowBox } from '../../WorkflowBox/WorkflowBox';
import { useIsEmbeddedContentBeingUsed } from '../DisplayWebContent/state/selectors/useIsEmbeddedContentBeingUsed';
import { useFeatureFlags } from 'core/app/AppContext';
import { useCampaignSchedulingSelector } from '../../../state/CampaignSettings/selectors/useCampaignSchedulingSelector';
import { AudienceSegmentsTooltip } from '../components/AudienceSegmentsTooltip';

const useShouldShowMissingFieldsWarning = () => {
  const { trigger, selectedTriggers } = useTrigger();
  const { getIsFocused, getIsTouched } = useWorkflowForm();
  const isEmbeddedContentBeingUsed = useIsEmbeddedContentBeingUsed();
  const { flagEnabled } = useFeatureFlags();

  const hasFailed = useMemo(() => {
    const failedValidation = [FrequencyRequiredRule(trigger)];

    if (!flagEnabled('ld-embedded-content') || !isEmbeddedContentBeingUsed) {
      failedValidation.push(AtLeastOneTriggerRequiredRule(trigger));
    }

    if (selectedTriggers.find(s => s === TriggerType.ExitIntent)) {
      failedValidation.push(
        AtLeastOneExitIntentRequiredRule({
          selectedTriggers: trigger.selectedTriggers,
          exitIntentAreas: trigger.exitIntentAreas,
        }),
      );
    }

    if (selectedTriggers.find(s => s === TriggerType.Inactivity)) {
      failedValidation.push(InactivityRequiredIfSelectedRule(trigger));
    }

    return !!failedValidation.filter(s => s.result === 'fail').length;
  }, [flagEnabled, isEmbeddedContentBeingUsed, selectedTriggers, trigger]);

  return useMemo(() => {
    return hasFailed && !getIsFocused('trigger') && getIsTouched('trigger');
  }, [hasFailed, getIsFocused, getIsTouched]);
};

const useShouldShowIntroText = () => {
  const { isIntroVariantShown } = useCanvas();
  const { trigger } = useTrigger();
  const hasSettings = useMemo(() => !!trigger.selectedTriggers?.length || !!trigger.segmentation, [
    trigger.segmentation,
    trigger.selectedTriggers?.length,
  ]);

  // Check if new campaign + has any settings set
  return useMemo(() => isIntroVariantShown && !hasSettings, [hasSettings, isIntroVariantShown]);
};

const useEventsSelected = () => {
  const { inactivity, selectedTriggers } = useTrigger();

  return useMemo(() => {
    const getTriggerDisplayText = (trigger: TriggerType) => {
      switch (trigger) {
        case TriggerType.Inactivity:
          return `${trigger.toString()}${!!inactivity ? ', ' + inactivity.toString() + 's' : ''}`;
        default:
          return trigger.toString();
      }
    };

    return selectedTriggers.map(getTriggerDisplayText).join(', ');
  }, [inactivity, selectedTriggers]);
};

export function TriggerNodeWorkflow({ id }) {
  const { flagEnabled } = useFeatureFlags();
  const { t } = useTranslation('campaigns');
  const { selectedTriggers, exitIntentAreas, segmentation, getFrequency } = useTrigger();
  const eventsSelected = useEventsSelected();

  const { actions } = useWorkflowForm();
  const { openDrawer } = useDrawer();

  const { startDate, startTime, endDate, endTime } = useCampaignSchedulingSelector();

  const openTrigger = () => {
    openDrawer(id, 'trigger');
    actions.setFocused('trigger', true);
  };

  const frequencyDisplayText = {
    'not set': 'Every visit',
    daily: 'Once a day',
    weekly: 'Once a week',
    monthly: 'Once a month',
    yearly: 'Once a year',
  };

  const frequency = useMemo(() => getFrequency(), [getFrequency]);

  const shouldShowMissingFieldsWarning = useShouldShowMissingFieldsWarning();
  const shouldShowIntroText = useShouldShowIntroText();

  const hasSelectedFrequency = useMemo(() => !!frequency, [frequency]);

  return (
    <WorkflowBox
      variant={shouldShowMissingFieldsWarning ? 'Incomplete' : 'Default'}
      id={id}
      ariaLabel={t('onsite.editor.workflow.triggerMessage.workflow.ariaLabel')}
    >
      <WorkflowBoxHeader
        icon={<SvgIcon name="bolt" size="32" color={theme.designToken.neutral700} />}
        title={t('onsite.editor.workflow.triggerMessage.workflow.title')}
        name={t('onsite.editor.workflow.triggerMessage.workflow.name')}
        tooltipProps={{
          title: t('onsite.editor.workflow.triggerMessage.workflow.titleTooltipTitle'),
          content: t('onsite.editor.workflow.triggerMessage.workflow.titleTooltipContent'),
        }}
        onOpenConfiguration={openTrigger}
        actionsMenuAriaLabel="Edit trigger settings"
        isActionMenuButtonOnly
      />
      {shouldShowIntroText ? (
        <P aria-label={t('onsite.editor.workflow.triggerMessage.workflow.introTextAriaLabel')}>
          {t('onsite.editor.workflow.triggerMessage.workflow.introText')}
        </P>
      ) : (
        <>
          <WorkflowLabel
            label={`${t('onsite.editor.workflow.triggerMessage.workflow.triggerType')}:`}
            text={t('onsite.editor.workflow.triggerMessage.workflow.event')}
          />

          {hasSelectedFrequency && (
            <WorkflowLabel label="Display Frequency:" text={frequencyDisplayText[frequency] || 'Every visit'} />
          )}

          {!!selectedTriggers.length && (
            <WorkflowLabel
              label={`${t('onsite.editor.workflow.triggerMessage.workflow.eventsSelected')}:`}
              text={eventsSelected}
            />
          )}

          {exitIntentAreas?.length === Object.values(ExitIntentAreas)?.length ? (
            <WorkflowLabel
              label={`${t('onsite.editor.workflow.triggerMessage.workflow.edgesSelected')}:`}
              text={t('onsite.editor.workflow.triggerMessage.workflow.allEdges')}
            />
          ) : exitIntentAreas?.length !== 0 ? (
            <WorkflowLabel
              label={`${t('onsite.editor.workflow.triggerMessage.workflow.edgesSelected')}:`}
              text={parseExitIntentAreas(exitIntentAreas).join(', ')}
            />
          ) : null}
          {segmentation ? (
            <WorkflowLabel
              label={`${t('onsite.editor.workflow.triggerMessage.workflow.audience')}:`}
              text={<AudienceSegmentsTooltip segmentation={segmentation} t={t} />}
            />
          ) : (
            <WorkflowLabel
              label={`${t('onsite.editor.workflow.triggerMessage.workflow.audience')}:`}
              text={t('onsite.editor.workflow.triggerMessage.workflow.audienceAllVisitors')}
            />
          )}

          {flagEnabled('ld-enable-onsite-scheduling') && (
            <>
              <WorkflowLabel
                label={'Starts:'}
                text={
                  startDate && startTime ? (
                    <>
                      {startDate.toLocaleDateString()} <strong>at</strong> {startTime} UTC
                    </>
                  ) : startDate ? (
                    startDate.toLocaleDateString()
                  ) : (
                    t('onsite.editor.workflow.scheduling.startDatePlaceholder')
                  )
                }
              />
              <WorkflowLabel
                label={'Ends:'}
                text={
                  endDate && endTime ? (
                    <>
                      {endDate.toLocaleDateString()} <strong>at</strong> {endTime} UTC
                    </>
                  ) : endDate ? (
                    endDate.toLocaleDateString()
                  ) : (
                    t('onsite.editor.workflow.scheduling.endDatePlaceholder')
                  )
                }
              />
            </>
          )}
        </>
      )}
      <Gap top={16} />
    </WorkflowBox>
  );
}
